import { useCallback, useEffect, useRef, useState } from 'react';
import { getCastingRecommendations, refreshRoleRecommendations } from '../../../../api/recommendationApi';
import { isBusy } from './recommendationModel';

export const POLL_INTERVAL_MS = 5000;
export const POLL_LIMIT_MS = 3 * 60 * 1000;
const DEFAULT_RATE_LIMIT_SEC = 60;

/** 기능이 꺼져 있거나 주인이 아닐 때 서버가 주는 응답 — 화면에서는 섹션을 통째로 숨긴다 */
const UNAVAILABLE_STATUSES = [401, 403, 404, 501, 503];

/**
 * 공고 하나의 배역별 추천을 읽고, 계산 중인 배역이 있으면 5초마다 다시 읽는다.
 * 3분이 지나도 끝나지 않으면 멈추고 timedOut을 알린다 (reload로 다시 시작).
 */
export function useRecommendations(postId) {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  // roleId → { requesting, message, blockedUntil }
  const [refreshState, setRefreshState] = useState({});

  const pollStartedAt = useRef(null);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  const load = useCallback(async () => {
    try {
      const { roles: next } = await getCastingRecommendations(postId);
      if (!alive.current) return;
      setRoles(next || []);
      setLoadError(false);
    } catch (err) {
      if (!alive.current) return;
      if (UNAVAILABLE_STATUSES.includes(err?.response?.status)) setUnavailable(true);
      else setLoadError(true);
    } finally {
      if (alive.current) setLoading(false);
    }
  }, [postId]);

  useEffect(() => {
    setLoading(true);
    setUnavailable(false);
    setTimedOut(false);
    pollStartedAt.current = null;
    load();
  }, [load]);

  const anyBusy = roles.some(r => isBusy(r.status));

  // 계산 중인 배역이 있는 동안만 주기적으로 읽는다
  useEffect(() => {
    if (!anyBusy) {
      pollStartedAt.current = null;
      return undefined;
    }
    if (timedOut) return undefined;
    if (pollStartedAt.current == null) pollStartedAt.current = Date.now();
    if (Date.now() - pollStartedAt.current >= POLL_LIMIT_MS) {
      setTimedOut(true);
      return undefined;
    }
    const timer = setTimeout(load, POLL_INTERVAL_MS);
    return () => clearTimeout(timer);
  }, [anyBusy, roles, timedOut, load]);

  const reload = useCallback(() => {
    pollStartedAt.current = null;
    setTimedOut(false);
    setLoadError(false);
    setLoading(true);
    load();
  }, [load]);

  const patchRefresh = (roleId, patch) => setRefreshState(prev => ({ ...prev, [roleId]: { ...prev[roleId], ...patch } }));

  const refresh = useCallback(async (roleId) => {
    patchRefresh(roleId, { requesting: true, message: null });
    try {
      await refreshRoleRecommendations(postId, roleId);
      if (!alive.current) return;
      patchRefresh(roleId, { requesting: false, message: null });
      // 서버가 곧 PENDING으로 바꾸므로 화면도 먼저 바꿔 두고 폴링을 새로 시작한다
      setRoles(prev => prev.map(r => (r.roleId === roleId ? { ...r, status: 'PENDING', error: null } : r)));
      pollStartedAt.current = null;
      setTimedOut(false);
    } catch (err) {
      if (!alive.current) return;
      const status = err?.response?.status;
      if (status === 429) {
        const retryAfter = Number(err?.response?.headers?.['retry-after']);
        const sec = Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : DEFAULT_RATE_LIMIT_SEC;
        patchRefresh(roleId, {
          requesting: false,
          blockedUntil: Date.now() + sec * 1000,
          message: `다시 추천은 배역마다 1분에 한 번 할 수 있어요. ${sec}초 뒤에 다시 눌러 주세요.`,
        });
      } else if (UNAVAILABLE_STATUSES.includes(status)) {
        setUnavailable(true);
      } else {
        patchRefresh(roleId, { requesting: false, message: '다시 추천을 요청하지 못했어요. 잠시 후 다시 시도해 주세요.' });
      }
    }
  }, [postId]);

  // 1분 제한이 풀리면 버튼을 다시 켠다
  useEffect(() => {
    const blocked = Object.entries(refreshState).filter(([, v]) => v?.blockedUntil && v.blockedUntil > Date.now());
    if (blocked.length === 0) return undefined;
    const soonest = Math.min(...blocked.map(([, v]) => v.blockedUntil));
    const timer = setTimeout(() => {
      setRefreshState(prev => {
        const next = { ...prev };
        Object.keys(next).forEach(k => {
          if (next[k]?.blockedUntil && next[k].blockedUntil <= Date.now()) next[k] = { ...next[k], blockedUntil: null, message: null };
        });
        return next;
      });
    }, Math.max(0, soonest - Date.now()) + 50);
    return () => clearTimeout(timer);
  }, [refreshState]);

  return { roles, loading, loadError, unavailable, timedOut, refreshState, refresh, reload };
}
